
	/* eslint-disable max-lines */
	import Vue from 'vue';
	import Component from 'vue-class-component';

	import { ComponentMap, Field, Form, FormErrors } from '@/types';
	import { Refs } from '@cnamts/vue-dot/src/types';
	import { mdiCurrencyEur  } from '@mdi/js';

	import {
		VAutocomplete,
		VCheckbox,
		VSelect,
		VTextarea,
		VTextField
	} from 'vuetify/lib';

	import { mdiEye, mdiEyeOff, mdiInformationOutline } from '@mdi/js';
	import getRules, { getRulesFn } from '@/mixins/ValidationRules';
	import datePickerOptionsFactory from '@/services/Factory/datePickerOptionsFactory';
	import formService from '@/services/form';
	import StringFormatter from '@/services/Formatter/StringFormatter';

	const Props = Vue.extend({
		props: {
			/** The v-model value */
			value: {
				type: [Array, Object],
				required: true
			},
			downloadingFile: {
				type: Boolean,
				default: false
			},
			/** Disable scroll to error */
			noScrollToError: {
				type: Boolean,
				default: false
			},
			disabled: {
				type: Boolean,
				default: false
			},
			displayOptionalFields: {
				type: Boolean,
				default: true
			},
			formRef: {
				type: String,
				default: 'form'
			}
		}
	});

	/** Create form with JSON model */
	@Component<CustomFormBuilder>({
		mixins: [
			getRules
		],
		model: {
			prop: 'value',
			event: 'change'
		},
		watch: {
			// When the v-model is updated
			value: {
				handler(value) {
					// Clone the new value with cloneDeep
					this.internalForm = value;
					this.$nextTick(() => {
						this.emitChangeEvent({} as any, 'init');
					});
				},
				// Fired when mounted
				immediate: true,
				deep: true
			}
		},
		// Import Vuetify components for dynamic components
		components: {
			VTextField,
			VTextarea,
			VSelect,
			VAutocomplete,
			VCheckbox
		}
	})
	export default class CustomFormBuilder extends Props {
		$refs!: Refs<{
			form: HTMLFormElement;
		}>;
		getRules!: getRulesFn;

		// Icons
		eyeIcon = mdiEye;
		eyeOffIcon = mdiEyeOff;
		infoIcon = mdiInformationOutline;
		showEyeIcon = false;
		isValid = false;
		errorsFromApi: Array<FormErrors> = [];
		currencyIcon = mdiCurrencyEur;
		/** The internal form */
		internalForm = {} as Form;

		timer = 0;

		/** The list of components to display by type of field */
		componentMap: ComponentMap = {
			string: 'VTextField',
			textarea: 'VTextarea',
			boolean: 'VCheckbox'
		};

		get placeholders() {
			const fieldPlaceholders = {} as any;

			for (const [fieldName, field] of Object.entries(this.internalForm)) {
				fieldPlaceholders[fieldName] = null;

				if (field.attr && field.attr.placeholder) {
					fieldPlaceholders[fieldName] = field.attr.placeholder;
				}
			}

			return fieldPlaceholders;
		}

		get eventListeners() {
			const eventListeners = {} as any;

			for (const [fieldName, field] of Object.entries(this.internalForm)) {
				eventListeners[fieldName] = null;

				if (field.attr && field.attr.eventListeners) {
					const fieldEventListener = {} as any;

					for (const [eventName, callBack] of Object.entries(field.attr.eventListeners)) {
						const formServiceFunction = callBack as keyof typeof formService;

						if ('function' === typeof formService[formServiceFunction]) {
							fieldEventListener[eventName] = formService[formServiceFunction];
						}
					}

					eventListeners[fieldName] = fieldEventListener;
				}
			}

			return eventListeners;
		}

		/** The list of components with "special" treatement */
		specialComponents = [
			'radio',
			'date',
			'password',
			'single_upload',
			'number',
			'boolean',
			'select',
			'autocomplete'
		];

		/**
		 * Transform the rules from the field
		 * By default it's an array of objects describing the rules,
		 * we transform this to an array of functions
		 */
		getValidationRules(field: Field) {
			// If we don't have rules, return an empty array
			if (!field.rules) {
				return [];
			}

			return this.getRules(field.rules);
		}

		/** Merge default options with field ones */
		getFieldOptions(field: Field) {
			return {
				validateOnBlur: true,
				outlined: true,
				...field.options
			};
		}

		getWidth(field: Field) {
			if (field.attr && field.attr.width) {
				return field.attr.width;
			}

			return 12;
		}

		fieldHasTooltip(field: Field): boolean {
			return field.attr?.tooltip;
		}

		getFieldDateOptions(field: Field) {
			const dataOptions = datePickerOptionsFactory.getOptions(field);

			// we create a new field with the new options to get the validationRules

			const textField = {
				rules: dataOptions.textField.rulesNames
			} as Field;

			dataOptions.textField.rules = this.getValidationRules(textField);

			return dataOptions;
		}

		/** Check that the component is "special" */
		ifSpecialComponent(type: string): boolean {
			return this.specialComponents.includes(type);
		}

		/** Get the component name for 'is' prop */
		getComponentName(type: string): string {
			return this.componentMap[type];
		}

		/** Scroll to the first error message */
		scrollToError(): void {
			// Wait for next render tick
			this.$nextTick(() => {
				// Find the first input in error
				const inputsInError = this.$refs.form.$el.querySelector(
					// Vuetify input error class
					'.v-input--has-state.error--text'
				);

				// If we find it,
				if (inputsInError) {
					// Use goTo() to scroll to the element
					this.$vuetify.goTo(inputsInError, {
						duration: 0
					});
				}
			});
		}

		get isDisabled(): boolean {
			return this.disabled;
		}

		/** Check if a field is required for display when attribute 'displayOptionalFields' is false */
		displayField(field: Field) {
			if (this.displayOptionalFields) {
				return true;
			}

			return undefined !== field.rules
				&& 0 !== Object.keys(field.rules).length;
		}

		public setErrorsFromApi(data: FormErrors[]): void {
			// For each field in error
			for (const [fieldName, value] of Object.entries(data)) {
				// If the field exists in our form and we have errors to set,
				// set errors
				if (
					this.internalForm &&
					this.internalForm[fieldName] &&
					value.errors
				) {
					// If the 'options' property doesn't exists, init it
					if (!this.internalForm[fieldName].options) {
						Vue.set(this.internalForm[fieldName], 'options', {});
					}

					// Set the error-messages prop with the errors from the API
					Vue.set(
						this.internalForm[fieldName].options,
						'errorMessages',
						value.errors
					);
				}
			}
			this.errorsFromApi = this.normalizerError(data);
		}

		normalizerError(node: any): any {
			if (Array.isArray(node)) {
				return node.map(this.normalizerError);
			}

			if (node && 'object' === typeof node) {
				if (node.children && 'object' === typeof node.children && !Array.isArray(node.children)) {
					return this.normalizerError(node.children);
				}

				const result = {} as any;
				Object.keys(node).forEach((key) => {
					result[key] = this.normalizerError(node[key]);
				});

				return result;
			}

			return node;
		}

		/** remove error-messages prop on the field in error */
		public removeErrorsFromApi(fieldName: any): void {
			// If the field exists in our form and we have errors to delete,
			if (
				this.internalForm &&
				this.internalForm[fieldName] &&
				this.internalForm[fieldName].options &&
				this.internalForm[fieldName].options.errorMessages
			) {
				// Delete the error-messages prop with the errors from the API
				Vue.delete(
					this.internalForm[fieldName].options,
					'errorMessages'
				);
			}
		}

		/**
		 * Update the v-model by emetting 'change' event
		 * Also emits 'refresh' event if the field is dynamic
		 */
		emitChangeEvent(field: Field, fieldName: string): void {
			this.$emit('change', this.internalForm);
			this.removeErrorsFromApi(fieldName);
			this.refreshForm(field);
		}

		/**
		 * Emits 'refresh' event if the field is dynamic.
		 */
		public refreshForm(field: Field) {
			if (field.dynamic) {
				if ('string' === field.type) {
					this.$emit('refresh');
				}

				if ('string' !== field.type) {
					this.$emit('refresh');
				}
			}
		}

		/**
		 * Catch enter key events
		 * Allows to do custom action per field
		 * and prevent default HTML <form> behavior
		 * when enter is pressed
		 */
		emitEnterEvent(fieldName: string): void {
			this.$emit(`enter:${fieldName}`, fieldName);
		}

		/** Validate the form & return the result */
		public validate(): boolean {
			// Validate the form
			const isValid = this.$refs.form.validate();

			if (!isValid && !this.noScrollToError) {
				// Scroll to first error
				this.scrollToError();
			}

			return isValid;
		}

		isCurrencyField(field: Field) {
			return 'currency' === field.attr?.['data-type'];
		}
		shouldDisplayDefaultField(field: Field) {
			return !this.ifSpecialComponent(field.type) && !this.isCurrencyField(field);
		}

		onNumberBlur(field: Field): void {
			if (!field.value) return;

			const num = parseFloat(String(field.value).replace(/\s/g, '').replace(',', '.'));
			if (isNaN(num)) return;

			field.value = StringFormatter.formatNumber(num, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			});
		}

		onNumberFocus(field: Field): void {
			if (!field.value) return;
			field.value = String(field.value).replace(/\s/g, '').replace(',', '.');
		}
	}
